import React, { useContext, useState, useEffect } from "react";
import { RemindersContext } from "../contexts/RemindersContext";
import {
  differenceInDays,
  isAfter,
  isSameDay,
  startOfDay,
  addDays,
  isBefore,
} from "date-fns";
import styled, { keyframes } from "styled-components";
import {
  FaCheck,
  FaRegCircle,
  FaCalendarAlt,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

/* ---------------------------------------------------
    ANIMATIONS
--------------------------------------------------- */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

/* ---------------------------------------------------
    STYLED COMPONENTS 
--------------------------------------------------- */

const Wrapper = styled.div`
  /* width: 30%; */
  background: var(--secondaryBackgroundColor);
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 20px;
  height: stretch;
  display: flex;
  flex-direction: column;
  color: var(--fontPrimaryColor);
  transition: all 0.5s ease;
  overflow: hidden;
  position: relative;
  min-height: 300px;
  /* @media (max-width: 1850px) {
    width: 37%;
  }
  @media (max-width: 1050px) {
    width: 100%;
  } */

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 20px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  padding-bottom: 10px;
  cursor: pointer;
  position: relative;
  color: ${(p) =>
    p.isActive ? "var(--fontPrimaryColor)" : "var(--fontSecondaryColor)"};
  display: flex;
  align-items: center;
  gap: 6px;

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 3px;
    background: var(--primaryColor);
    border-radius: 3px 3px 0 0;
    transform: scaleX(${(p) => (p.isActive ? 1 : 0)});
    transition: transform 0.3s ease;
    transform-origin: left;
  }
`;

const Badge = styled.span`
  background: var(--primaryColor);
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 700;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
`;

const ListContainer = styled.div`
  animation: ${fadeIn} 0.4s ease;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 40px;
  color: var(--fontSecondaryColor);
  font-size: 14px;
  opacity: 0.8;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0;
  margin: 0;
  padding-bottom: 40px;
`;

const ReminderItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  font-size: 14px;
  color: var(--fontPrimaryColor);
  list-style: none;

  .text {
    flex: 1;
    text-decoration: ${(props) =>
      props.isCompleted ? "line-through" : "none"};
    opacity: ${(props) => (props.isCompleted ? 0.5 : 1)};
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--fontPrimaryColor);
  }
`;

const UpcomingItem = styled(ReminderItem)`
  justify-content: space-between;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.05);
  .left-col {
    display: flex;
    align-items: center;
    gap: 7px;
  }
`;

const CheckButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.isCompleted ? "#10b981" : "var(--primaryColor)")};
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--fontSecondaryColor);
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  opacity: 0.5;

  &:hover {
    opacity: 1;
    color: #ef4444;
    background-color: rgba(239, 68, 68, 0.1);
  }
`;

const DaysLeft = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--fontSecondaryColor);
  white-space: nowrap;
  background: rgba(0, 0, 0, 0.04);
  padding: 4px 8px;
  border-radius: 6px;
`;

const AddButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: var(--primaryColor);
  color: white;
  font-size: 18px;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
    background: var(--darkPrimaryColor);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  backdrop-filter: blur(10px) brightness(0.7);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Modal = styled.div`
  background: var(--popupColor);
  padding: 1.6rem;
  width: 500px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: ${popIn} 0.25s ease;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

  @media (max-width: 550px) {
    width: 90%;
  }
`;

const FormTitle = styled.h3`
  margin: 0;
  text-align: center;
  font-weight: 500;
  color: var(--fontPrimaryColor);
`;

const Input = styled.input`
  padding: 15px 20px;
  border-radius: 5px;
  border: none;
  width: 100%;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  background: var(--thirdBackgroundColor);
  outline: none;
  color: var(--fontPrimaryColor);
  box-shadow: 0 0 10px 1px var(--popupShadow);
  &::placeholder {
    color: var(--fontSecondaryColor);
  }
`;

const PopupButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;

  & button {
    padding: 0.7rem 1.1rem;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-weight: 500;
    font-family: "Montserrat", sans-serif;
  }

  & button:first-child {
    background: var(--thirdBackgroundColor);
    color: var(--fontPrimaryColor);
  }

  & button:last-child {
    background: var(--fontPrimaryColor);
    color: var(--thirdBackgroundColor);
  }
`;

const StatusText = styled.span`
  font-size: 10px;
  color: var(--fontSecondaryColor);
  font-style: italic;
  margin-left: 8px;
`;

/* ---------------------------------------------------
    COMPONENT LOGIC
--------------------------------------------------- */

export default function DashboardReminders() {
  const { t } = useTranslation();
  const { reminders, addReminder, toggleReminder, deleteReminder } =
    useContext(RemindersContext);

  const [activeTab, setActiveTab] = useState("today");
  const [showModal, setShowModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDate, setNewDate] = useState("");

  const todayStart = startOfDay(new Date());
  const tomorrowStart = startOfDay(addDays(new Date(), 1));

  const getDateObj = (rDate) =>
    rDate instanceof Date ? rDate : new Date(rDate);

  const getCompletedDate = (reminder) => {
    if (!reminder.completedAt) return null;
    return startOfDay(new Date(reminder.completedAt));
  };

  const todaysList = reminders.filter((r) => {
    const rDate = getDateObj(r.date);
    if (isNaN(rDate.getTime())) return false;

    const isForToday = isSameDay(startOfDay(rDate), todayStart);
    const completedDate = getCompletedDate(r);

    return (
      isForToday ||
      (r.isCompleted && completedDate && isSameDay(completedDate, todayStart))
    );
  });

  const upcomingList = reminders
    .filter((r) => {
      const rDate = getDateObj(r.date);
      if (isNaN(rDate.getTime())) return false;
      return isAfter(startOfDay(rDate), todayStart) && !r.isCompleted;
    })
    .sort((a, b) => {
      const aDate = getDateObj(a.date);
      const bDate = getDateObj(b.date);
      return aDate - bDate;
    })
    .slice(0, 5);

  useEffect(() => {
    if (todaysList.length === 0 && reminders.length > 0)
      setActiveTab("upcoming");
    else if (todaysList.length > 0) setActiveTab("today");
  }, [todaysList.length, reminders.length]);

  const handleAdd = (e) => {
    e.preventDefault();

    if (!newTitle.trim() || !newDate) return;

    addReminder(newTitle, newDate);

    setNewTitle("");
    setNewDate("");
    setShowModal(false);
    setActiveTab("today");
  };

  const shouldShowCompleted = (reminder) => {
    const completedDate = getCompletedDate(reminder);
    if (!completedDate) return true;

    const yesterdayStart = startOfDay(addDays(new Date(), -1));
    return (
      isSameDay(completedDate, todayStart) ||
      isSameDay(completedDate, yesterdayStart)
    );
  };

  return (
    <Wrapper>
      {/* Tabs */}
      <TabContainer>
        <TabButton
          isActive={activeTab === "today"}
          onClick={() => setActiveTab("today")}
        >
          {t("reminders.today")}{" "}
          {todaysList.length > 0 && <Badge>{todaysList.length}</Badge>}
        </TabButton>
        <TabButton
          isActive={activeTab === "upcoming"}
          onClick={() => setActiveTab("upcoming")}
        >
          {t("reminders.upcoming")}
        </TabButton>
      </TabContainer>

      {/* Content */}
      <ContentArea>
        {activeTab === "today" && (
          <ListContainer>
            {todaysList.length === 0 ? (
              <EmptyState>
                <FaCheck size={24} style={{ opacity: 0.3 }} />
                <p>{t("reminders.allCaughtUp")}</p>
              </EmptyState>
            ) : (
              <List>
                {todaysList
                  .sort((a, b) => {
                    if (a.isCompleted !== b.isCompleted) {
                      return a.isCompleted ? 1 : -1;
                    }
                    return new Date(a.date) - new Date(b.date);
                  })
                  .map((r) => {
                    const rDate = getDateObj(r.date);
                    const completedDate = getCompletedDate(r);
                    const isForToday = isSameDay(startOfDay(rDate), todayStart);

                    return (
                      <ReminderItem key={r.id} isCompleted={r.isCompleted}>
                        <CheckButton
                          onClick={() => toggleReminder(r.id)}
                          isCompleted={r.isCompleted}
                        >
                          {r.isCompleted ? (
                            <FaCheck size={10} />
                          ) : (
                            <FaRegCircle size={12} />
                          )}
                        </CheckButton>
                        <span className="text">
                          {r.title}
                          {r.isCompleted && completedDate && !isForToday && (
                            <StatusText>
                              {t("reminders.completedToday")}
                            </StatusText>
                          )}
                        </span>
                        <DeleteButton onClick={() => deleteReminder(r.id)}>
                          <FaTrash size={10} />
                        </DeleteButton>
                      </ReminderItem>
                    );
                  })}
              </List>
            )}
          </ListContainer>
        )}

        {activeTab === "upcoming" && (
          <ListContainer>
            {upcomingList.length === 0 ? (
              <EmptyState>
                <FaCalendarAlt size={24} style={{ opacity: 0.3 }} />
                <p>{t("reminders.noUpcoming")}</p>
              </EmptyState>
            ) : (
              <List>
                {upcomingList.map((r) => {
                  const rDate = getDateObj(r.date);
                  const daysLeft = differenceInDays(
                    startOfDay(rDate),
                    todayStart
                  );

                  return (
                    <UpcomingItem key={r.id}>
                      <div className="left-col">
                        <CheckButton onClick={() => toggleReminder(r.id)}>
                          <FaRegCircle size={12} />
                        </CheckButton>
                        <span className="text">{r.title}</span>
                      </div>
                      <DaysLeft>
                        {daysLeft === 1
                          ? t("reminders.tomorrow")
                          : t("reminders.inDays", { days: daysLeft })}
                      </DaysLeft>
                    </UpcomingItem>
                  );
                })}
              </List>
            )}
          </ListContainer>
        )}
      </ContentArea>

      {/* Add Button */}
      <AddButton onClick={() => setShowModal(true)}>
        <FaPlus />
      </AddButton>

      {/* Add Reminder Popup */}
      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <FormTitle>{t("reminders.addNew")}</FormTitle>

            <Input
              placeholder={t("reminders.titlePlaceholder")}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              autoFocus
            />

            <Input
              placeholder={t("reminders.chooseDeadline")}
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
            />

            <PopupButtons>
              <button onClick={() => setShowModal(false)}>
                {t("reminders.cancel")}
              </button>
              <button onClick={handleAdd}>{t("reminders.save")}</button>
            </PopupButtons>
          </Modal>
        </ModalOverlay>
      )}
    </Wrapper>
  );
}
